import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, LogIn } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { claimOwnerAdmin } from "@/lib/leads.functions";
import { Section } from "@/components/Section";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in — Freelancer Tamal" },
      { name: "description", content: "Private sign-in for the Freelancer Tamal site owner." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Sign in — Freelancer Tamal" },
      { property: "og:description", content: "Private sign-in for the Freelancer Tamal site owner." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "err" | "check-email">("idle");
  const [errMsg, setErrMsg] = useState("");
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const claimAdmin = useServerFn(claimOwnerAdmin);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) void navigate({ to: "/admin/leads" });
    });
  }, [navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState("loading");
    if (mode === "signup") {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { emailRedirectTo: `${window.location.origin}/auth` },
      });
      if (error) {
        setState("err");
        setErrMsg(error.message);
        return;
      }
      if (!data.session) {
        setState("check-email");
        return;
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) {
        setState("err");
        setErrMsg(error.message);
        return;
      }
    }
    // Idempotent: only the owner email is ever granted the admin role.
    try {
      await claimAdmin();
    } catch {
      /* non-fatal — the leads page reports the missing role */
    }
    void navigate({ to: "/admin/leads" });
  };

  return (
    <>
      <h1 className="sr-only">Sign in</h1>
      <Section eyebrow="Private" title={<>Owner <span className="text-gradient">sign-in.</span></>}>
        <p className="max-w-xl text-muted-foreground -mt-8">
          This area is for the site owner. If you were looking for the contact
          form, it lives on the contact page.
        </p>
        <form onSubmit={onSubmit} className="mt-10 max-w-md space-y-4 rounded-3xl border border-border bg-card p-8">
          <div>
            <label htmlFor="auth-email" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</label>
            <input
              id="auth-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:border-primary/60"
            />
          </div>
          <div>
            <label htmlFor="auth-password" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Password</label>
            <input
              id="auth-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:border-primary/60"
            />
          </div>
          {state === "err" && <p role="alert" className="text-sm text-destructive">{errMsg}</p>}
          {state === "check-email" && (
            <p role="status" className="text-sm text-primary">
              Account created. Confirm it from the email you just received, then sign in.
            </p>
          )}
          <button
            type="submit"
            disabled={state === "loading"}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium disabled:opacity-60"
          >
            {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn size={16} />}{" "}
            {mode === "signup" ? "Create account" : "Sign in"}
          </button>
          <button
            type="button"
            onClick={() => {
              setMode(mode === "signup" ? "signin" : "signup");
              setState("idle");
            }}
            className="block text-xs text-muted-foreground underline"
          >
            {mode === "signup" ? "Already have an account? Sign in" : "First time? Create the owner account"}
          </button>
        </form>
      </Section>
    </>
  );
}
